import {
    Font,
    Document,
    Page,
    Text,
    View,
    StyleSheet,
} from '@react-pdf/renderer';
import moment from 'moment';

import {
    ReportPDFIndividualCardInfo,
    ReportPDFOverallCardInfo,
    ReportItemType,
    ReportTimeRangeType,
    PDFIndividualInsight,
    ReportPDFSummaryInfo,
    ReportPDFFilterItems,
    ReportPDFOverviewData,
    ReportItemSummaryType,
    ReportPDFIndividualData,
} from 'types/Reports';
import { DATE_TIME_FORMATS } from 'constant';
import { REPORT_SENSOR_REGISTRY, REPORT_SUMMARY_ITEMS } from 'constant/report';

import ReportPDFSummary from 'components/reportPDF/ReportPDFSummary';
import ReportPDFOverallUsageCard from 'components/reportPDF/ReportPDFOverallUsageCard';
import ReportPDFIndividualUsageCard from 'components/reportPDF/ReportPDFIndividualUsageCard';
import ReportPDFPageHeader from 'components/reportPDF/ReportPDFPageHeader';
import ReportPDFPageFooter from 'components/reportPDF/ReportPDFPageFooter';
import ReportPDFFilterDescription from 'components/reportPDF/ReportPDFFilterDescription';
import ReportPDFIndividualItem from 'components/reportPDF/ReportPDFIndividualItem';
import ReportPDFFilterDetail from 'components/reportPDF/ReportPDFFilterDetail';

/* ************************************************************************** */
/* FONTs */
/* ************************************************************************** */
Font.register({ family: 'Roboto', src: 'fonts/roboto/Roboto-Light.ttf', fontWeight: 300, fontStyle: 'normal' });
Font.register({ family: 'Roboto', src: 'fonts/roboto/Roboto-Regular.ttf', fontWeight: 400, fontStyle: 'normal' });
Font.register({ family: 'Roboto', src: 'fonts/roboto/Roboto-Medium.ttf', fontWeight: 500, fontStyle: 'normal' });
Font.register({ family: 'Roboto', src: 'fonts/roboto/Roboto-SemiBold.ttf', fontWeight: 600, fontStyle: 'normal' });
Font.register({ family: 'Roboto', src: 'fonts/roboto/Roboto-Bold.ttf', fontWeight: 700, fontStyle: 'normal' });

/* ************************************************************************** */
/* STYLES */
/* ************************************************************************** */
const itemPageStyles = StyleSheet.create({
    itemPageTitle: {
        width: '80%',
        position: 'relative',
        fontFamily: 'Roboto',
        fontSize: 12,
        fontWeight: 500,
        color: '#333333',
        marginTop: 15,
        marginBottom: 15,
    },
});
const styles = StyleSheet.create({
    page: {
        paddingTop: 85,
        paddingBottom: 52,
        paddingLeft: 20,
        paddingRight: 20,
        fontFamily: 'Roboto',
    },
    body: {
        fontFamily: 'Roboto',
        position: 'relative',
        margin: 0,
        padding: 0,
        width: '100%',
    },
});

/* ************************************************************************** */
/* FILTER & SUMMARY PAGE */
/* ************************************************************************** */

type FilterSummaryPageProps = {
    organizationName: string;
    reportPeriod: string;
    timeRangeType: ReportTimeRangeType;
    resource: string;
    generatedDate: string;
    summaryInfo: ReportPDFSummaryInfo;
};

const FilterSummaryPage = ({
    organizationName,
    reportPeriod,
    timeRangeType,
    resource,
    generatedDate,
    summaryInfo,
}: FilterSummaryPageProps) => {
    return (
        <Page size="A4" orientation="portrait" style={styles.page}>
            <ReportPDFPageHeader organizationName={organizationName} reportPeriod={reportPeriod} />

            <ReportPDFPageFooter />

            <ReportPDFFilterDescription
                reportPeriod={reportPeriod}
                timeRangeType={timeRangeType}
                resource={resource}
                generatedDate={generatedDate}
            />

            <ReportPDFSummary
                timeRangeType={timeRangeType}
                summaryInfo={summaryInfo}
            />
        </Page>
    );
};

/* ************************************************************************** */
/* FILTER DETAILS PAGE */
/* ************************************************************************** */

type FilterDescriptionPageProps = {
    organizationName: string;
    reportPeriod: string;
    filterItems: ReportPDFFilterItems;
};

const FilterDescriptionPage = ({
    organizationName,
    reportPeriod,
    filterItems,
}: FilterDescriptionPageProps) => {

    return (
        <Page size="A4" orientation="portrait" style={styles.page}>
            <ReportPDFPageHeader organizationName={organizationName} reportPeriod={reportPeriod} />

            <ReportPDFPageFooter />

            <View style={styles.body}>
                <ReportPDFFilterDetail filterItems={filterItems} />

                <Text style={{
                    marginTop: 20,
                    fontSize: 10,
                    fontFamily: 'Roboto',
                    lineHeight: 1,
                    color: '#636363',
                }}>
                    *The value is rounded for display. Actual precision is higher.
                </Text>
            </View>
        </Page>
    );
};

/* ************************************************************************** */
/* Overview ITEM PAGE */
/* ************************************************************************** */

type OverviewItemPageProps = {
    title: string;
    organizationName: string;
    overallCardTitle: string;
    individualCardTitle: string;

    timeRangeType: ReportTimeRangeType;
    itemType: ReportItemSummaryType;

    itemOverallInfo: ReportPDFOverallCardInfo;
    itemIndividualInfo: ReportPDFIndividualCardInfo;

    unit: string;

    numberOfItems: number;

    color: string;
    dateFormat: string;
    reportPeriod: string;
};

const OverviewItemPage = ({
    title,
    organizationName,
    overallCardTitle,
    individualCardTitle,

    timeRangeType,
    itemType,

    itemOverallInfo,
    itemIndividualInfo,

    unit,

    numberOfItems,

    color,
    dateFormat,
    reportPeriod,
}: OverviewItemPageProps) => {
    return (
        <Page size="A4" orientation="portrait" style={styles.page}>
            <ReportPDFPageHeader organizationName={organizationName} reportPeriod={reportPeriod} />

            <ReportPDFPageFooter />

            <View style={styles.body}>
                <Text style={itemPageStyles.itemPageTitle}>
                    {title}
                </Text>

                <ReportPDFOverallUsageCard
                    title={overallCardTitle}
                    timeRangeType={timeRangeType}
                    itemType={itemType}
                    item={itemOverallInfo}
                    unit={unit}
                    numberOfItems={numberOfItems}
                    color={color}
                    dateFormat={dateFormat}
                    reportPeriod={reportPeriod}
                />

                <ReportPDFIndividualUsageCard
                    title={individualCardTitle}
                    timeRangeType={timeRangeType}
                    itemType={itemType}
                    item={itemIndividualInfo}
                    unit={unit}
                    color={color}
                    reportPeriod={reportPeriod}
                />
            </View>
        </Page>
    );
};

/* ************************************************************************** */
/* Individual ITEM PAGE */
/* ************************************************************************** */

type IndividualItemPageProps = {
    title: string;
    organizationName: string;

    itemType: ReportItemType;
    timeRangeType: ReportTimeRangeType;

    colorHex: string;

    unit: string;

    itemInsights: PDFIndividualInsight[];

    time: moment.Moment;

    dateFormat: string;
    reportPeriod: string;
};

const IndividualItemPage = ({
    title,
    organizationName,

    itemType,
    timeRangeType,

    colorHex,

    unit,

    itemInsights,

    time,

    dateFormat,
    reportPeriod,
}: IndividualItemPageProps) => {
    const list = [];

    let i = 0;
    let length = itemInsights.length;

    while (i < length) {
        const children = [];
        children.push(
            <ReportPDFIndividualItem
                key={`report-pdf-individual-item-${itemType}-${i}`}
                itemType={itemType}
                timeRangeType={timeRangeType}
                itemName={itemInsights[i].name}
                colorHex={colorHex}
                unit={unit}
                data={itemInsights[i].data}
                prevData={itemInsights[i].prevData}
                categories={itemInsights[i].categories}
                time={time}
                dateFormat={dateFormat}
                reportPeriod={reportPeriod}
                isSecond={false}
            />
        );

        i++;

        if (i < length) {
            children.push(
                <ReportPDFIndividualItem
                    key={`report-pdf-individual-item-${itemType}-${i}`}
                    itemType={itemType}
                    timeRangeType={timeRangeType}
                    itemName={itemInsights[i].name}
                    colorHex={colorHex}
                    unit={unit}
                    data={itemInsights[i].data}
                    prevData={itemInsights[i].prevData}
                    categories={itemInsights[i].categories}
                    time={time}
                    dateFormat={dateFormat}
                    reportPeriod={reportPeriod}
                    isSecond={true}
                />
            );

            i++;
        }

        list.push(
            <Page size="A4" orientation="portrait" style={styles.page} key={`insights-${itemType}-${i}`}>
                <ReportPDFPageHeader organizationName={organizationName} reportPeriod={reportPeriod} />

                <ReportPDFPageFooter />

                <View style={styles.body}>
                    <Text style={itemPageStyles.itemPageTitle}>
                        {title}
                    </Text>

                    {children}
                </View>
            </Page>
        );
    }

    return <>{list}</>;
};

/* ************************************************************************** */
/* MAIN */
/* ************************************************************************** */

type ReportPDFProps = {
    organizationName: string;
    reportPeriod: string;
    timeRangeType: ReportTimeRangeType;
    resource: string;

    summaryInfo: ReportPDFSummaryInfo;
    overviewInfo: ReportPDFOverviewData;
    individualInfo: ReportPDFIndividualData;
    filterItems: ReportPDFFilterItems;
};

export default function ReportPDF({
    organizationName,
    reportPeriod,
    timeRangeType,
    resource,

    summaryInfo,
    overviewInfo,
    individualInfo,
    filterItems,
}: Readonly<ReportPDFProps>) {
    const dateFormat = DATE_TIME_FORMATS.DATE_ONLY;

    const creationDate = moment();
    const generatedDate = creationDate.format(dateFormat);

    const overviewPages = [];

    for (const type of REPORT_SUMMARY_ITEMS) {
        const info = overviewInfo[type];
        if (info) {
            const entry = REPORT_SENSOR_REGISTRY[type];
            overviewPages.push(<OverviewItemPage
                key={`overview-item-page-${type}`}
                title={entry.pdfOverview!.title}
                organizationName={organizationName}

                overallCardTitle={entry.pdfOverview!.overallCardTitle}
                individualCardTitle={entry.pdfOverview!.individualCardTitle}

                timeRangeType={timeRangeType}
                itemType={type}

                itemOverallInfo={info.overallData}
                itemIndividualInfo={info.individualData}

                unit={entry.unit}
                numberOfItems={filterItems[type].length}
                color={entry.colorHex}

                dateFormat={dateFormat}
                reportPeriod={reportPeriod}
            />);
        }
    }

    const individualPages = [];

    const types = Object.keys(individualInfo) as ReportItemType[];
    const time = moment(reportPeriod, DATE_TIME_FORMATS.MONTH_ONLY);

    for (const type of types) {
        if (individualInfo[type].length > 0) {
            const entry = REPORT_SENSOR_REGISTRY[type];
            individualPages.push(<IndividualItemPage
                key={`individual-item-page-${type}`}
                title={entry.pdfIndividualTitle}
                organizationName={organizationName}

                itemType={type}
                timeRangeType={timeRangeType}

                colorHex={entry.colorHex}

                unit={entry.unit}

                itemInsights={individualInfo[type]}

                time={time}

                dateFormat={dateFormat}
                reportPeriod={reportPeriod}
            />);
        }
    }

    return (
        <Document
            title='USAGE REPORT'
            author='BRT Systems Pte Ltd'
            subject='SmartOffice '
            keywords='IOTP'
            creator='IOTP'
            producer='IOTP'
            pdfVersion='1.7'
            creationDate={creationDate.toDate()}
        >
            <FilterSummaryPage
                organizationName={organizationName}
                reportPeriod={reportPeriod}
                timeRangeType={timeRangeType}
                resource={resource}
                generatedDate={generatedDate}
                summaryInfo={summaryInfo}
            />

            {overviewPages}

            {individualPages}

            <FilterDescriptionPage
                organizationName={organizationName}
                reportPeriod={reportPeriod}
                filterItems={filterItems}
            />
        </Document>
    );
};
